import { useEffect, useState, useCallback } from "react";
import { notificationHubManager } from "../services/signalr/notification-hub-manager";
import { VendingStepData } from "../model/notifications/notifications";

/**
 * Custom hook for subscribing to vending status updates (callback-based approach)
 *
 * @param callback - Optional callback function to execute when a vending status update is received
 * @returns The latest vending status update received, or null if none
 *
 * @example
 * // Track vending progress
 * const [steps, setSteps] = useState<string[]>([]);
 * useVendingStatusListener((data) => {
 *   setSteps(prev => [...prev, data.subTypeOfEvent]);
 *   toast.info(`Vending: ${data.subTypeOfEvent}`);
 * });
 *
 * @example
 * // Display latest status
 * const latestStatus = useVendingStatusListener();
 * if (latestStatus) {
 *   return <StatusBadge>{latestStatus.subTypeOfEvent}</StatusBadge>;
 * }
 */
export const useVendingStatusListener = (
	callback?: (data: VendingStepData) => void
) => {
	const [latestUpdate, setLatestUpdate] = useState<VendingStepData | null>(null);

	// Memoize callback to prevent unnecessary re-subscriptions
	const stableCallback = useCallback(
		(data: VendingStepData) => {
			setLatestUpdate(data);
			callback?.(data);
		},
		[callback]
	);

	useEffect(() => {
		// Subscribe to vending status updates
		const unsubscribe = notificationHubManager.subscribeToVendingStatus(stableCallback);

		// Cleanup: unsubscribe on unmount
		return () => {
			unsubscribe();
		};
	}, [stableCallback]);

	return latestUpdate;
};
